// src/pages/admin/MainSettings.tsx
import { FormEvent, useEffect, useState } from "react";
import { authFetch } from "../../lib/auth";

type SiteFlags = {
  maintenance_enabled: boolean;
  maintenance_message: string;
  updated_at?: string;
};

export default function MainSettings() {
  const [flags, setFlags] = useState<SiteFlags>({
    maintenance_enabled: false,
    maintenance_message: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await authFetch("/api/admin/site-flags");
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;

        if (!res.ok || !data?.ok) {
          throw new Error(data?.message || "설정을 불러오지 못했습니다.");
        }

        const f = data.flags || {};
        setFlags({
          maintenance_enabled: !!f.maintenance_enabled,
          maintenance_message: f.maintenance_message ?? "",
          updated_at: f.updated_at ?? undefined,
        });
      } catch (e) {
        console.error(e);
        if (!cancelled) setError("설정을 불러오지 못했습니다.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const res = await authFetch("/api/admin/site-flags", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          maintenance_enabled: flags.maintenance_enabled,
          maintenance_message: flags.maintenance_message,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        throw new Error(data?.message || "저장 실패");
      }

      setSavedAt(new Date().toLocaleString());
    } catch (e) {
      console.error(e);
      setError(
        e instanceof Error ? e.message : "저장에 실패했습니다. 다시 시도해 주세요."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-slate-500">
        설정을 불러오는 중입니다...
      </div>
    );
  }

  return (
    <div className="jp-admin-page jp-admin-settings max-w-2xl space-y-6">
      <div className="jp-admin-settings-heading">
        <h1 className="text-lg font-semibold text-slate-900">
          사이트 점검 설정
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          점검 기간 동안 일반 사용자는 &quot;작업 중&quot; 안내 화면만 보이게
          할 수 있습니다. 관리자는 계속 접속 가능합니다.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="space-y-5 rounded-xl border border-slate-200 bg-white p-4 sm:p-5"
      >
        <div className="flex items-start gap-3">
          <input
            id="maintenance_enabled"
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600"
            checked={flags.maintenance_enabled}
            onChange={(e) =>
              setFlags((prev) => ({
                ...prev,
                maintenance_enabled: e.target.checked,
              }))
            }
          />
          <div>
            <label
              htmlFor="maintenance_enabled"
              className="text-sm font-medium text-slate-900"
            >
              화면을 &quot;작업 중&quot; 모드로 전환
            </label>
            <p className="mt-1 text-xs text-slate-500">
              체크 시 사용자용 모든 페이지 대신 작업 중 안내 화면만 노출됩니다.
            </p>
          </div>
        </div>

        <div className="space-y-1">
          <label
            htmlFor="maintenance_message"
            className="text-sm font-medium text-slate-900"
          >
            안내 문구 (한 줄)
          </label>
          <input
            id="maintenance_message"
            type="text"
            maxLength={200}
            placeholder="예) 카드 결제 서비스 점검으로 오후 3시부터 이용 가능합니다."
            value={flags.maintenance_message}
            onChange={(e) =>
              setFlags((prev) => ({
                ...prev,
                maintenance_message: e.target.value,
              }))
            }
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          <p className="text-xs text-slate-400">
            비워두면 기본 문구만 표시됩니다. (최대 200자)
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between gap-4">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {saving ? "저장 중..." : "설정 저장"}
          </button>
          {savedAt && (
            <span className="text-xs text-slate-400">
              마지막 저장: {savedAt}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
